import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNav } from "../components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const orderColor = (status) => {
  if (status === "paid") return "#1C1A17";
  if (status === "rejected") return "#7A2E2E";
  return "#6E6A5E"; // pending
};

const itnColor = (outcome) => {
  if (outcome === "paid_and_fulfilled" || outcome === "already_paid") return "#1C1A17";
  return "#7A2E2E"; // any failure/attention outcome
};

const statusLabel = (o) => {
  if (o.status === "paid") return o.preorder ? "Pre-order · awaiting despatch" : "Paid · ready to despatch";
  return o.status;
};

const AdminBook = () => {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [itnLog, setItnLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = typeof window !== "undefined" ? localStorage.getItem("rq_admin_token") : null;

  const load = React.useCallback(() => {
    if (!token) {
      navigate("/admin");
      return Promise.resolve();
    }
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    return Promise.all([
      axios.get(`${API}/book/subscribers`, auth),
      axios.get(`${API}/book/orders`, auth),
      axios.get(`${API}/book/itn-log`, auth),
    ])
      .then(([s, o, l]) => {
        setSubscribers(s.data);
        setOrders(o.data);
        setItnLog(l.data);
      })
      .catch(() => {
        localStorage.removeItem("rq_admin_token");
        navigate("/admin");
      });
  }, [navigate, token]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const exportCsv = (path, filename) => async () => {
    try {
      const res = await fetch(`${API}/${path}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* no-op */
    }
  };
  const exportSubs = exportCsv("book/subscribers/export", "book-subscribers.csv");
  const exportOrders = exportCsv("book/orders/export", "book-orders.csv");

  const deleteOrder = (o) => async () => {
    const label = `${o.full_name || "(no name)"} · R${o.amount}`;
    if (!window.confirm(`Delete this order?\n\n${label}\n\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/book/orders/${o.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
    } catch {
      alert("Could not delete that order. Please try again.");
    }
  };

  const fulfilOrder = (o) => async () => {
    const paid = o.status === "paid";
    const verb = paid ? "re-send the confirmation / download link for" : "mark as PAID and send the link for";
    if (!window.confirm(`This will ${verb}:\n\n${o.full_name || "(no name)"} · R${o.amount} · ${o.email}\n\nOnly do this once you have confirmed the payment in your PayFast dashboard.`)) return;
    try {
      const res = await axios.post(`${API}/book/orders/${o.id}/fulfil`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status: "paid" } : x)));
      if (res.data && res.data.download_url) {
        window.prompt("Done. The buyer has been emailed this link. Copy it here if you want to send it yourself:", res.data.download_url);
      } else {
        alert("Done. The confirmation email has been sent to the buyer.");
      }
    } catch {
      alert("Could not fulfil that order. Please try again.");
    }
  };

  const paidCount = orders.filter((o) => o.status === "paid").length;

  return (
    <div className="min-h-screen bg-[#F4F1EA] pb-24" data-testid="admin-book-page">
      <AdminNav active="book" />

      <div className="mx-auto max-w-[900px] px-6 md:px-8 pt-14">
        <h1 className="text-[28px] md:text-[32px] italic text-[#1C1A17]">The Book</h1>
        <p className="mt-4 text-[14px] text-[#6E6A5E]">
          No One Is Coming To Save You &middot; chapter requests and direct orders.
        </p>

        {/* Orders */}
        <div className="mt-14">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E]">Direct orders</h2>
            <div className="flex items-baseline gap-5">
              <span className="text-[12px] text-[#6E6A5E]" data-testid="admin-book-order-count">
                {loading ? "Loading" : `${paidCount} paid of ${orders.length}`}
              </span>
              <button onClick={exportOrders} className="text-[11px] uppercase tracking-[0.28em] text-[#1C1A17] hover:text-[#7A2E2E] transition-colors" data-testid="admin-book-orders-export">
                Export Orders
              </button>
            </div>
          </div>
          <div className="mt-6 border-t" style={{ borderColor: "rgba(168,162,150,0.4)" }}>
            {!loading && orders.length === 0 && (
              <p className="py-8 text-[15px] italic text-[#6E6A5E]" data-testid="admin-book-orders-empty">
                No orders have been placed yet.
              </p>
            )}
            {orders.map((o) => (
              <div key={o.id} className="py-5 border-b" style={{ borderColor: "rgba(168,162,150,0.4)" }} data-testid="admin-book-order">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-4">
                    <span className="text-[16px] text-[#1C1A17]">{o.full_name || "(no name)"}</span>
                    <span className="text-[14px] text-[#1C1A17]">R{o.amount}</span>
                    <span className="text-[10px] uppercase tracking-[0.28em]" style={{ color: orderColor(o.status) }}>{statusLabel(o)}</span>
                    <span className="text-[11px] text-[#A8A296] uppercase tracking-[0.2em]">{o.mode}</span>
                  </div>
                  <span className="text-[12px] text-[#6E6A5E]">{formatDate(o.created_at)}</span>
                </div>
                <div className="mt-2 text-[13px] text-[#6E6A5E] leading-relaxed">
                  <span className="uppercase tracking-[0.18em] text-[11px] text-[#7A2E2E]">{o.product === "ebook" ? "Ebook" : "Paperback"}</span>
                  <span> &middot; {o.quantity}&times; &middot; </span>
                  <span>{o.product === "ebook" ? "Instant download" : o.delivery_method === "collect" ? "Collect in Centurion" : o.delivery_method === "courier_sadc" ? "Courier to SADC" : "Courier within SA"}</span>
                  {o.email && <> &middot; <a href={`mailto:${o.email}`} className="hover:text-[#7A2E2E]">{o.email}</a></>}
                  {o.mobile && <> &middot; {o.mobile}</>}
                  {(o.delivery_method === "courier_sa" || o.delivery_method === "courier_sadc") && (o.address || o.city) && (
                    <div className="mt-1">{[o.address, o.suburb, o.city, o.province, o.country, o.postal_code].filter(Boolean).join(", ")}</div>
                  )}
                  {o.note && <div className="mt-1 italic">Note: {o.note}</div>}
                </div>
                <div className="mt-2 flex gap-6">
                  <button
                    onClick={fulfilOrder(o)}
                    data-testid="admin-book-order-fulfil"
                    className="text-[11px] uppercase tracking-[0.2em] text-[#7A2E2E] hover:text-[#B0573F] transition-colors"
                  >
                    {o.status === "paid" ? (o.product === "ebook" ? "Resend link" : "Resend confirmation") : "Mark paid & send link"}
                  </button>
                  <button
                    onClick={deleteOrder(o)}
                    data-testid="admin-book-order-delete"
                    className="text-[11px] uppercase tracking-[0.2em] text-[#A8A296] hover:text-[#7A2E2E] transition-colors"
                  >
                    Delete order
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PayFast ITN diagnostics */}
        <div className="mt-16">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E]">Payment callbacks · PayFast ITN</h2>
            <button onClick={() => load()} className="text-[11px] uppercase tracking-[0.28em] text-[#1C1A17] hover:text-[#7A2E2E] transition-colors" data-testid="admin-itn-refresh">
              Refresh
            </button>
          </div>
          <p className="mt-2 text-[12px] text-[#6E6A5E]">
            Every confirmation PayFast sends us is logged here. After a test payment, tap Refresh to see exactly what happened.
          </p>
          <div className="mt-6 border-t" style={{ borderColor: "rgba(168,162,150,0.4)" }}>
            {!loading && itnLog.length === 0 && (
              <p className="py-8 text-[15px] italic text-[#6E6A5E]" data-testid="admin-itn-empty">
                No PayFast callbacks received yet.
              </p>
            )}
            {itnLog.map((e) => (
              <div key={e.id} className="py-4 border-b" style={{ borderColor: "rgba(168,162,150,0.4)" }} data-testid="admin-itn-entry">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-[12px] uppercase tracking-[0.2em]" style={{ color: itnColor(e.outcome) }}>{e.outcome || "—"}</span>
                  <span className="text-[12px] text-[#6E6A5E]">{formatDate(e.at)}</span>
                </div>
                <div className="mt-1 text-[12px] text-[#6E6A5E]">
                  order {e.order_id ? e.order_id.slice(0, 8) : "—"} &middot; {e.payment_status || "—"} &middot; signature{" "}
                  {e.sig_match === true ? "matched" : e.sig_match === false ? "MISMATCH" : "—"} &middot; postback {e.postback || "—"} &middot; from {e.ip || "—"}
                </div>
                {Array.isArray(e.steps) && e.steps.length > 0 && (
                  <div className="mt-1 text-[11px] text-[#A8A296] leading-relaxed">{e.steps.join("  →  ")}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Subscribers */}
        <div className="mt-16">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E]">Free-chapter list</h2>
            <div className="flex items-baseline gap-5">
              <span className="text-[12px] text-[#6E6A5E]" data-testid="admin-book-sub-count">
                {loading ? "Loading" : `${subscribers.length} ${subscribers.length === 1 ? "reader" : "readers"}`}
              </span>
              <button onClick={exportSubs} className="text-[11px] uppercase tracking-[0.28em] text-[#1C1A17] hover:text-[#7A2E2E] transition-colors" data-testid="admin-book-export">
                Export List
              </button>
            </div>
          </div>
          <div className="mt-6 border-t" style={{ borderColor: "rgba(168,162,150,0.4)" }}>
            {!loading && subscribers.length === 0 && (
              <p className="py-8 text-[15px] italic text-[#6E6A5E]" data-testid="admin-book-subs-empty">
                No one has requested the chapter yet.
              </p>
            )}
            {subscribers.map((s) => (
              <div key={s.id} className="py-4 border-b flex flex-wrap items-baseline justify-between gap-2" style={{ borderColor: "rgba(168,162,150,0.4)" }} data-testid="admin-book-subscriber">
                <a href={`mailto:${s.email}`} className="text-[15px] text-[#1C1A17] hover:text-[#7A2E2E] transition-colors">
                  {s.first_name ? `${s.first_name} · ${s.email}` : s.email}
                </a>
                <span className="text-[12px] text-[#6E6A5E]">{s.source || "free-sample"} · {formatDate(s.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBook;
